import React, { ReactNode, Ref } from 'react';

// material-ui
import Card, { CardProps } from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent, { CardContentProps } from '@mui/material/CardContent';
import CardHeader, { CardHeaderProps } from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';

// project imports
import { ThemeMode } from 'config';
import useConfig from 'hooks/useConfig';

interface SubCardProps {
  children: ReactNode | string | null;
  content?: boolean;
  className?: string;
  contentClass?: string;
  darkTitle?: boolean;
  secondary?: ReactNode | string;
  sx?: CardProps['sx'];
  contentSX?: CardContentProps['sx'];
  headerSX?: CardHeaderProps['sx'];
  footerSX?: {};
  title?: ReactNode | string;
  actions?: ReactNode | string;
  flexibleContent?: boolean;
  onClick?: () => void;
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onFocus?: () => void;
}

// ==============================|| CUSTOM SUB CARD ||============================== //

const SubCard = React.forwardRef(
  (
    {
      children,
      className,
      content = true,
      contentClass,
      darkTitle,
      secondary,
      sx = {},
      contentSX = {},
      headerSX = {},
      footerSX = {},
      title,
      actions,
      flexibleContent,
      onClick,
      tabIndex,
      onKeyDown,
      onFocus,
      ...others
    }: SubCardProps,
    ref: Ref<HTMLDivElement>,
  ) => {
    const { mode } = useConfig();
    const defaultShadow =
      mode === ThemeMode.DARK ? '0 2px 14px 0 rgb(33 150 243 / 10%)' : '0 2px 14px 0 rgb(32 40 45 / 8%)';

    return (
      <Card
        ref={ref}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          ':hover': { boxShadow: defaultShadow },
          ...(flexibleContent && {
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }),
          ...sx,
        }}
        onClick={onClick}
        tabIndex={tabIndex}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        {...others}
      >
        {/* card header and action — כותרת מחרוזת עטופה בטיפוגרפיה; ReactNode מוצג כמו שהוא */}
        {!darkTitle && title && (
          <CardHeader
            sx={{ p: 2.5, ...headerSX }}
            title={
              typeof title === 'string' ? (
                <Typography variant="h5">{title}</Typography>
              ) : (
                title
              )
            }
            action={secondary}
          />
        )}
        {darkTitle && title && (
          <CardHeader
            sx={{ p: 2.5, ...headerSX }}
            title={
              typeof title === 'string' ? (
                <Typography variant="h4">{title}</Typography>
              ) : (
                title
              )
            }
            action={secondary}
          />
        )}

        {/* content & header divider */}
        {title && <Divider />}

        {/* card content */}
        {content && (
          <CardContent
            sx={{
              p: 2.5,
              ...(flexibleContent && {
                flex: '1 1 0%',
                minHeight: 0,
                overflow: 'hidden',
              }),
              ...contentSX,
            }}
            className={contentClass || ''}
          >
            {children}
          </CardContent>
        )}
        {!content && children}

        {/* actions & footer divider */}
        {actions && <Divider />}

        {actions && <CardActions sx={{ p: 2.5, ...footerSX }}>{actions}</CardActions>}
      </Card>
    );
  },
);

export default SubCard;
